import { useState, useEffect } from 'react'
import {
  X,
  CheckCircle2,
  Settings,
  Zap,
  Clock,
  Coins,
  Star,
  Book,
  Info,
  AlertCircle,
  Tag,
  Link,
} from 'lucide-react'
import type { AIService } from '@/lib/supabase-client'
import { SERVICE_TYPES, getServiceType } from '@/constants/service-types'
import { SERVICE_CATEGORIES } from '@/constants/service-categories'
import { Select, type SelectOption } from '@/components/ui/Select'
import { ServiceLogo } from '@/components/ServiceLogo'

interface FormData {
  name: string
  description: string
  detailed_description: string
  provider: string
  service_id: string
  api_endpoint: string
  api_key_encrypted: string
  points_cost: number
  max_tokens: number
  timeout_seconds: number
  status: 'active' | 'inactive'
  icon_url: string
  category: string
  features: string[]
  usage_instructions: string
}

interface ServiceEditModalProps {
  service: AIService | null
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: FormData) => Promise<void>
  isSubmitting: boolean
}

export function ServiceEditModal({ service, isOpen, onClose, onSubmit, isSubmitting }: ServiceEditModalProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    detailed_description: '',
    provider: 'coze-bot',
    service_id: '',
    api_endpoint: '',
    api_key_encrypted: '',
    points_cost: 10,
    max_tokens: 4096,
    timeout_seconds: 30,
    status: 'active',
    icon_url: '',
    category: 'text',
    features: [],
    usage_instructions: '',
  })

  const [featureInput, setFeatureInput] = useState('')

  // 初始化表单数据
  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name,
        description: service.description || '',
        detailed_description: service.detailed_description || '',
        provider: service.provider,
        service_id: service.service_id,
        api_endpoint: service.api_endpoint,
        api_key_encrypted: service.api_key_encrypted || '',
        points_cost: service.points_cost,
        max_tokens: service.max_tokens,
        timeout_seconds: service.timeout_seconds,
        status: service.status,
        icon_url: service.icon_url || '',
        category: service.category || 'text',
        features: service.features || [],
        usage_instructions: service.usage_instructions || '',
      })
    } else {
      // 重置为默认值
      setFormData({
        name: '',
        description: '',
        detailed_description: '',
        provider: 'coze-bot',
        service_id: '',
        api_endpoint: '',
        api_key_encrypted: '',
        points_cost: 10,
        max_tokens: 4096,
        timeout_seconds: 30,
        status: 'active',
        icon_url: '',
        category: 'text',
        features: [],
        usage_instructions: '',
      })
    }
  }, [service, isOpen])

  // 检查是否需要API配置
  const needsApiConfig = formData.provider !== 'coze-bot' && formData.provider !== 'coze-workflow'

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, featureInput.trim()]
      }))
      setFeatureInput('')
    }
  }

  const removeFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(formData)
  }

  if (!isOpen) return null

  const typeOptions: SelectOption[] = SERVICE_TYPES.map(type => {
    return {
      value: type.id,
      label: type.displayName,
      description: type.description,
      icon: <ServiceLogo provider={type.id} size="sm" />
    }
  })

  const categoryOptions: SelectOption[] = SERVICE_CATEGORIES.map(cat => {
    const Icon = cat.icon
    return {
      value: cat.id,
      label: cat.name,
      description: cat.description,
      icon: <Icon className="w-4 h-4" />
    }
  })

  const statusOptions: SelectOption[] = [
    {
      value: 'active',
      label: '启用',
      description: '服务正常运行'
    },
    {
      value: 'inactive',
      label: '停用',
      description: '服务暂停使用'
    }
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6" />
            {service ? '编辑服务' : '新建服务'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-8">
            {/* 核心配置 - 第一优先级 */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Tag className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">核心配置</h3>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">必填</span>
              </div>

              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700 mb-1.5 block">服务名称 *</span>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="例如：Claude 3.5 对话助手"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm font-medium text-gray-700 mb-1.5 block">服务类型 *</span>
                    <Select
                      value={formData.provider}
                      onChange={(value) => setFormData(prev => ({ ...prev, provider: value }))}
                      options={typeOptions}
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="text-sm font-medium text-gray-700 mb-1.5 block">简短描述 *</span>
                  <input
                    type="text"
                    required
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="一句话介绍此服务"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-gray-700 mb-1.5 block">功能分类 *</span>
                  <Select
                    value={formData.category}
                    onChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                    options={categoryOptions}
                  />
                </label>
              </div>
            </div>

            {/* API 配置 - 第二优先级 */}
            <div className="border-2 border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                  <Link className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">API 配置</h3>
                {!needsApiConfig && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    Coze 服务无需配置
                  </span>
                )}
              </div>

              {needsApiConfig ? (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="block">
                      <span className="text-sm font-medium text-gray-700 mb-1.5 block">服务 ID *</span>
                      <input
                        type="text"
                        required
                        value={formData.service_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, service_id: e.target.value }))}
                        placeholder="例如：7428xxx"
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      />
                    </label>

                    <label className="block">
                      <span className="text-sm font-medium text-gray-700 mb-1.5 block">API 端点 *</span>
                      <input
                        type="url"
                        required
                        value={formData.api_endpoint}
                        onChange={(e) => setFormData(prev => ({ ...prev, api_endpoint: e.target.value }))}
                        placeholder="https://api.example.com/v1"
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                      />
                    </label>
                  </div>

                  <label className="block">
                    <span className="text-sm font-medium text-gray-700 mb-1.5 block">API 密钥</span>
                    <input
                      type="password"
                      value={formData.api_key_encrypted || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, api_key_encrypted: e.target.value }))}
                      placeholder="pat_xxx..."
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all font-mono text-sm"
                    />
                  </label>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Coze 服务说明</p>
                      <p>Coze 智能体和工作流通过 Coze API 统一调用，只需填写服务 ID，无需配置独立的 API 端点。</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Service ID for Coze */}
              {!needsApiConfig && (
                <div className="mt-4 space-y-4">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700 mb-1.5 block">服务 ID *</span>
                    <input
                      type="text"
                      required
                      value={formData.service_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, service_id: e.target.value }))}
                      placeholder="Coze Bot ID 或 Workflow ID"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                    />
                    <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      在 Coze 平台获取 Bot 或 Workflow 的 ID
                    </p>
                  </label>

                  <label className="block">
                    <span className="text-sm font-medium text-gray-700 mb-1.5 block">API 密钥 *</span>
                    <input
                      type="password"
                      required
                      value={formData.api_key_encrypted || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, api_key_encrypted: e.target.value }))}
                      placeholder="pat_xxx..."
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Coze 平台的 Personal Access Token
                    </p>
                  </label>
                </div>
              )}
            </div>

            {/* 资源限制 - 第三优先级 */}
            <div className="border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-5 h-5 text-yellow-600" />
                <h3 className="text-base font-semibold text-gray-900">资源限制</h3>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                    <Coins className="w-3.5 h-3.5" />
                    积分消耗 *
                  </span>
                  <input
                    type="number"
                    min={0}
                    required
                    value={formData.points_cost}
                    onChange={(e) => setFormData(prev => ({ ...prev, points_cost: parseInt(e.target.value) || 0 }))}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-gray-700 mb-1.5 block">最大 Tokens *</span>
                  <input
                    type="number"
                    min={1}
                    required
                    value={formData.max_tokens}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_tokens: parseInt(e.target.value) || 1 }))}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    超时 (秒) *
                  </span>
                  <input
                    type="number"
                    min={1}
                    required
                    value={formData.timeout_seconds}
                    onChange={(e) => setFormData(prev => ({ ...prev, timeout_seconds: parseInt(e.target.value) || 1 }))}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                  />
                </label>
              </div>
            </div>

            {/* 详细信息 - 第四优先级，可折叠 */}
            <details className="border border-gray-200 rounded-xl overflow-hidden">
              <summary className="cursor-pointer bg-gray-50 px-6 py-4 hover:bg-gray-100 transition flex items-center gap-2">
                <Book className="w-5 h-5 text-gray-600" />
                <h3 className="text-base font-semibold text-gray-900">详细描述与使用说明</h3>
                <span className="text-xs text-gray-500 ml-auto">点击展开</span>
              </summary>
              <div className="p-6 space-y-4">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700 mb-1.5 block">图标 URL</span>
                  <input
                    type="url"
                    value={formData.icon_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, icon_url: e.target.value }))}
                    placeholder="https://example.com/icon.png"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                  />
                  <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    留空则显示服务类型默认图标
                  </p>
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-gray-700 mb-1.5 block">详细描述</span>
                  <textarea
                    value={formData.detailed_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, detailed_description: e.target.value }))}
                    placeholder="详细介绍此服务的功能、特点、适用场景等..."
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-gray-700 mb-1.5 block">使用说明</span>
                  <textarea
                    value={formData.usage_instructions}
                    onChange={(e) => setFormData(prev => ({ ...prev, usage_instructions: e.target.value }))}
                    placeholder="如何使用此服务，参数说明，注意事项等..."
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                  />
                </label>
              </div>
            </details>

            {/* 功能特性 - 第五优先级，可折叠 */}
            <details className="border border-gray-200 rounded-xl overflow-hidden">
              <summary className="cursor-pointer bg-gray-50 px-6 py-4 hover:bg-gray-100 transition flex items-center gap-2">
                <Star className="w-5 h-5 text-gray-600" />
                <h3 className="text-base font-semibold text-gray-900">功能特性</h3>
                <span className="text-xs text-gray-500 ml-auto">点击展开</span>
              </summary>
              <div className="p-6 space-y-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                    placeholder="输入特性后按回车添加"
                    className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
                  />
                  <button
                    type="button"
                    onClick={addFeature}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                  >
                    添加
                  </button>
                </div>
                {formData.features.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.features.map((feature, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm border border-blue-200"
                      >
                        {feature}
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="hover:text-blue-900"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </details>

            {/* 服务状态 */}
            <div className="border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Settings className="w-5 h-5 text-gray-600" />
                <h3 className="text-base font-semibold text-gray-900">服务状态</h3>
              </div>
              <Select
                value={formData.status}
                onChange={(value) => setFormData(prev => ({ ...prev, status: value as 'active' | 'inactive' }))}
                options={statusOptions}
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            取消
          </button>
          <button
            onClick={(e) => {
              e.preventDefault()
              const form = document.querySelector('form')
              if (form) {
                form.requestSubmit()
              }
            }}
            disabled={isSubmitting}
            className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                保存中...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                保存
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
